import { Injectable, OnDestroy, signal } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { FormControl, FormGroup } from "@angular/forms";
import { ServiceLinkHttpService } from "./service-link-http.service";
import {
  CustomerViewItemResponse,
  CustomerViewResponse,
  ProposalStatusEnum
} from "src/domain/service-link/serviceLinkClient";
import { environment } from "src/environments/environment";

export type ProposalTouchedItem = {
  item: CustomerViewItemResponse;
  value: boolean;
};

@Injectable({
  providedIn: "root"
})
export class ServiceLinkStoreService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private _proposal = signal<CustomerViewResponse | undefined>(undefined);

  public form = new FormGroup({});

  public get proposal() {
    return this._proposal.asReadonly();
  }

  public setProposal(value: CustomerViewResponse | undefined) {
    this._proposal.set(value);
  }

  public isPreview = false;

  private touchedProposalsSub: BehaviorSubject<ProposalTouchedItem[]> =
    new BehaviorSubject<ProposalTouchedItem[]>([]);

  public touchedProposals$ = this.touchedProposalsSub.asObservable();

  private selectedProposals$ = this.touchedProposals$.pipe(
    map((x) => x.filter((i) => i.value === true).map((i) => i.item))
  );

  proposalPriceSum$: Observable<number> = this.selectedProposals$.pipe(
    map((selectedProposals) =>
      selectedProposals
        .map((proposal) => proposal.price || 0)
        .reduce((acc, price) => acc + price, 0)
    )
  );

  constructor(private serviceLinkHttpService: ServiceLinkHttpService) {}

  async load(proposalId: string, isPreview: boolean = false) {
    this.setProposal(undefined);
    this.isPreview = isPreview;
    this.form = new FormGroup({});

    await this.serviceLinkHttpService.get(proposalId).then((proposal) => {
      this.setProposal(proposal);
      (this._proposal()?.proposalItems ?? []).forEach((x) => {
        if (x.id) {
          this.form.addControl(x.id.toString(), new FormControl(null));
        }
      });
    });
  }

  updateFormValues() {
    const proposal = this._proposal();
    if (!proposal) return;

    (proposal.proposalItems ?? []).forEach((item) => {
      const control = this.form.controls[item.id.toString()];
      if (control) {
        control.setValue(item.customerAccepted);
      } else {
        console.warn(`Control with id ${item.id.toString()} does not exist.`);
      }
    });
  }

  isStatusValid(): boolean {
    const proposal = this._proposal();
    return (
      proposal?.status === ProposalStatusEnum.Sent ||
      proposal?.status === ProposalStatusEnum.Opened
    );
  }

  hasCustomerAnswered(): boolean {
    const proposal = this._proposal();
    return proposal?.status === ProposalStatusEnum.CustomerAnswered;
  }

  getStatus(): ProposalStatusEnum {
    const proposal = this._proposal();
    if (!proposal?.status) {
      throw new Error("No proposal loaded");
    }
    return proposal?.status;
  }

  async initialize() {
    this.form.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        map((currentValues: Record<string, boolean>) => {
          const proposal = this._proposal();
          return (proposal?.proposalItems ?? []).map(
            (item) =>
              ({
                item,
                value: currentValues[item.id]
              }) as ProposalTouchedItem
          );
        })
      )
      .subscribe((result) => {
        this.touchedProposalsSub.next(result);
      });
  }

  respond(): Promise<any> {
    const proposal = this._proposal();
    if (!proposal?.id) {
      return Promise.reject("No proposal loaded");
    }
    const selectedItemIds = this.touchedProposalsSub
      .getValue()
      .filter((x) => x.value === true)
      .map((x) => x.item.id);

    return this.serviceLinkHttpService
      .respond(proposal.id, selectedItemIds)
      .then(() => {
        const updatedProposal = new CustomerViewResponse(proposal);
        updatedProposal.status = ProposalStatusEnum.CustomerAnswered;
        this.setProposal(updatedProposal);
      })
      .catch((error) => {
        if (environment.production === false) {
          console.log(error);
        }
      });
  }

  respondNotNow(): Promise<any> {
    const proposal = this._proposal();
    if (!proposal?.id) {
      return Promise.reject("No proposal loaded");
    }
    return this.serviceLinkHttpService
      .respond(proposal.id, [])
      .then(() => {
        const updatedProposal = new CustomerViewResponse(proposal);
        updatedProposal.status = ProposalStatusEnum.CustomerAnswered;
        this.setProposal(updatedProposal);
      })
      .catch((error) => {
        if (environment.production === false) {
          console.log(error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
